import {
	EcommerceProduct,
	EcommerceProductVariantQuantity,
	ProductPageSeoData,
	ProductPagePriceData,
	EcommerceProductReviewsAnalytics,
} from '@zyro-inc/site-modules/types';
import { formatPrice } from '@zyro-inc/site-modules/utils/ecommerce/priceFormatter';
import { removeHtmlTags } from '@zyro-inc/site-modules/utils/removeHtmlTags';
import { getIsProductInStock } from '@zyro-inc/site-modules/components/blocks/ecommerce/utils';

type ProductSchema = {
	'@context': string;
	'@type': string;
	name: string;
	image: string;
	description: string;
	offers?: {
		'@type'?: string;
		url?: string;
		priceCurrency?: string;
		price?: string;
		lowPrice?: string;
		highPrice?: string;
		availability?: string;
	};
	aggregateRating?: {
		'@type': 'AggregateRating';
		ratingValue: string;
		ratingCount: string;
	};
};

export const getProductJsonLd = ({
	seoData,
	priceData,
	pageSlug,
	domain,
	reviewsAnalytics,
}: {
	seoData: ProductPageSeoData;
	priceData: ProductPagePriceData;
	pageSlug: string;
	domain: string;
	reviewsAnalytics?: EcommerceProductReviewsAnalytics
}): ProductSchema => {
	const productSchema: ProductSchema = {
		'@context': 'https://schema.org/',
		'@type': 'Product',
		name: priceData?.productTitle,
		image: seoData?.meta.ogImagePath || '',
		description: seoData?.meta.description ? removeHtmlTags(seoData.meta.description).trim() : '',
		offers: {},
	};

	if (reviewsAnalytics && reviewsAnalytics.total_reviews > 0) {
		productSchema.aggregateRating = {
			'@type': 'AggregateRating',
			ratingValue: `${reviewsAnalytics.average_rating}`,
			ratingCount: `${reviewsAnalytics.total_reviews}`,
		};
	}

	if (!priceData) {
		return productSchema;
	}

	const priceAmount = formatPrice({
		amount: priceData.firstVariantPriceDiscounted ?? priceData.firstVariantPrice ?? priceData.lowestAmount,
		currency: priceData.currency,
		isPriceDisplayedWithCurrency: false,
	});

	productSchema.offers = {
		'@type': 'Offer',
		url: `https://${domain}/${pageSlug}`,
		priceCurrency: priceData.currency.code,
		price: priceAmount,
		availability: 'https://schema.org/InStock',
	};

	return productSchema;
};

export const updateProductJsonLd = (
	product: EcommerceProduct,
	variantsQuantity: Array<EcommerceProductVariantQuantity>,
) => {
	const scripts = document.head.getElementsByTagName('script');
	// eslint-disable-next-line unicorn/prefer-spread
	const script: HTMLScriptElement | undefined = Array.from(scripts).find(
		(s): s is HTMLScriptElement => s.type === 'application/ld+json' && !!s.textContent?.includes('https://schema.org/'),
	);

	if (!script || !script.textContent) {
		return;
	}

	const schemaJsonLd = JSON.parse(script.textContent);

	const isProductWithVariants = product.variants.length > 1;
	const isProductInStock = getIsProductInStock({
		product,
		variantsQuantity,
	});

	// Availability is already set on astro build
	if (!isProductWithVariants && isProductInStock) {
		return;
	}

	// Since we dont handle variants yet, do not set availability for them.
	if (isProductWithVariants) {
		delete schemaJsonLd.offers.availability;
	} else if (schemaJsonLd.offers) {
		schemaJsonLd.offers.availability = 'https://schema.org/OutOfStock';
	}

	script.textContent = JSON.stringify(schemaJsonLd);
	document.head.removeChild(script);

	const newScript = document.createElement('script');

	newScript.type = 'application/ld+json';
	newScript.textContent = JSON.stringify(schemaJsonLd);
	document.head.appendChild(newScript);
};
